import React from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  formatCurrency,
  formatNumber,
  getRoyalties,
  resaleOrderWasPurchased,
} from '../../../utilities/helpers'
import AuthService from '../../../utilities/services/auth.service'
import { useModal } from '../../../providers/ModalProvider/ModalProvider'

import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'

import { EmptyContainer } from '../../EmptyContainer'
import { ExportBtn } from '../../ExportBtn'
import ListingDetailsModal from './ListingDetailsModal'
import ResaleOrderDetailsModal from './ResaleOrderDetailsModal'
import './resaleDetailsModal.scss'
import {
  listingFor,
  listingIdFor,
  listingQuantity,
  orderStatus,
  ticketType,
} from './resaleOrderDisplay'

const STATUS_OPTIONS = [
  { label: 'Complete', value: 'complete' },
  { label: 'Failed', value: 'failed' },
  { label: 'Incomplete', value: 'incomplete' },
]

const DEFAULT_STATUS = 'complete'

function statusFromSearchParams(searchParams) {
  const value = searchParams.get('status')
  return STATUS_OPTIONS.some((option) => option.value === value)
    ? value
    : DEFAULT_STATUS
}

export default function ResaleTickets({ orders }) {
  const { openModal } = useModal()
  const [searchParams, setSearchParams] = useSearchParams()
  const status = statusFromSearchParams(searchParams)
  const statusLabel =
    STATUS_OPTIONS.find((option) => option.value === status)?.label ||
    'Complete'

  const setStatus = (nextStatus) => {
    const next = new URLSearchParams(searchParams)
    next.set('status', nextStatus)
    setSearchParams(next, { replace: true })
  }

  const filteredOrders = (orders || []).filter(
    (order) => String(order?.status || '').toLowerCase() === status
  )

  const getSum = (arr) => arr.reduce((pv, cv) => pv + cv, 0)

  const totalQuantity = () => getSum(filteredOrders.map(listingQuantity))

  const totalRoyalties = () =>
    getSum(
      filteredOrders
        .filter(resaleOrderWasPurchased)
        .map((order) => getRoyalties(order))
    )

  const organizationUUIDFor = (order) =>
    order?.organizationUUID || AuthService.getOrg()?.[0]?.uuid || ''

  const openListingDetails = (order) => {
    openModal(
      ListingDetailsModal,
      {
        listingId: listingIdFor(order),
        organizationUUID: organizationUUIDFor(order),
      },
      { className: 'modal-lg resale-details-modal', scrollable: true }
    )
  }

  const openOrderDetails = (order) => {
    openModal(
      ResaleOrderDetailsModal,
      {
        order,
        organizationUUID: organizationUUIDFor(order),
      },
      { className: 'modal-lg resale-details-modal', scrollable: true }
    )
  }

  return (
    <section>
      <header className="section-header">
        <div className="actions-group-flex">
          <FloatingLabel controlId="resale-status" label="Status">
            <Form.Select
              name="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
          <ExportBtn data={filteredOrders} exportedData="sales" />
        </div>
      </header>
      {filteredOrders.length === 0 ? (
        <Card body>
          <EmptyContainer style="center lg">
            <p>No {statusLabel.toLowerCase()} resale tickets.</p>
          </EmptyContainer>
        </Card>
      ) : (
        <div className="table-container">
          <Table>
            <thead>
              <tr>
                <th>Listing ID</th>
                <th>Order ID</th>
                <th>Ticket type</th>
                <th>Price</th>
                <th>Quantity</th>
                <th>Royalties</th>
                <th>Status</th>
                <th>Purchased</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order, index) => {
                const badge = orderStatus(order)
                const purchased = resaleOrderWasPurchased(order)
                const listingId = listingIdFor(order)
                return (
                  <tr key={index}>
                    <td>
                      {listingId ? (
                        <Button
                          variant="link"
                          type="button"
                          className="p-0 text-link"
                          aria-label={`Listing ${listingId}`}
                          onClick={() => openListingDetails(order)}
                        >
                          {listingId}
                        </Button>
                      ) : null}
                    </td>
                    <td>
                      {order?.id && order?.orderId ? (
                        <Button
                          variant="link"
                          type="button"
                          className="p-0 text-link"
                          aria-label={`Order ${order.orderId}`}
                          onClick={() => openOrderDetails(order)}
                        >
                          {order.orderId}
                        </Button>
                      ) : (
                        order?.orderId || ''
                      )}
                    </td>
                    <td>{ticketType(order)}</td>
                    <td>{formatCurrency(listingFor(order)?.askingPrice)}</td>
                    <td>{formatNumber(listingQuantity(order))}</td>
                    <td>
                      {formatCurrency(purchased ? getRoyalties(order) : 0)}
                    </td>
                    <td>
                      {badge ? (
                        <Badge
                          bg="none"
                          className={`badge-sm badge--${badge.value}`}
                        >
                          {badge.label}
                        </Badge>
                      ) : null}
                    </td>
                    <td>
                      <Badge
                        bg="none"
                        className={`badge-sm badge--${purchased ? 'paid' : 'released'}`}
                      >
                        {purchased ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                  </tr>
                )
              })}
              <tr className="total-row">
                <td colSpan={4}>Total</td>
                <td>{formatNumber(totalQuantity())}</td>
                <td>{formatCurrency(totalRoyalties())}</td>
                <td />
                <td />
              </tr>
            </tbody>
          </Table>
        </div>
      )}
    </section>
  )
}
